import { AnimateOnScroll } from "@/components/AnimateOnScroll";
import { LucideIcon } from "lucide-react";

interface Industry {
  name: string;
  icon: LucideIcon;
  projects: number;
  percentage: number;
}

interface StatBox {
  icon: LucideIcon;
  value: string;
  label: string;
  gradient?: string; // If provided, uses gradient bg; otherwise uses card style
  iconColor?: string; // e.g., "blue" for text-blue-500
}

interface IndustriesSectionProps {
  badge: {
    icon: LucideIcon;
    text: string;
    color: string;
  };
  title: string;
  subtitle?: string;
  industries: Industry[];
  progressGradient: string; // e.g., "from-blue-500 to-indigo-500"
  progressIconColor: string; // e.g., "blue"
  statBoxes: [StatBox, StatBox, StatBox, StatBox]; // Exactly 4 stat boxes
}

export const IndustriesSection = ({
  badge,
  title,
  subtitle,
  industries,
  progressGradient,
  progressIconColor,
  statBoxes,
}: IndustriesSectionProps) => {
  const BadgeIcon = badge.icon;

  return (
    <section className="py-12 md:py-20 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
          <AnimateOnScroll animation="slide-right">
            <div>
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full bg-${badge.color}-100 dark:bg-${badge.color}-900/30 mb-3 md:mb-4`}>
                <BadgeIcon className={`w-3.5 h-3.5 md:w-4 md:h-4 text-${badge.color}-600`} />
                <span className={`text-${badge.color}-700 dark:text-${badge.color}-400 text-xs md:text-sm font-medium`}>{badge.text}</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 md:mb-4">
                {title}
              </h2>
              {subtitle && (
                <p className="text-sm sm:text-base md:text-lg text-muted-foreground mb-6 md:mb-8">
                  {subtitle}
                </p>
              )}
              
              <div className="space-y-4 md:space-y-6">
                {industries.map((industry, index) => {
                  const IndustryIcon = industry.icon;
                  return (
                    <div key={index}>
                      <div className="flex items-center justify-between mb-1.5 md:mb-2">
                        <div className="flex items-center gap-2 md:gap-3">
                          <IndustryIcon className={`w-4 h-4 md:w-5 md:h-5 text-${progressIconColor}-600`} />
                          <span className="font-medium text-foreground text-sm md:text-base">{industry.name}</span>
                        </div>
                        <span className="text-xs md:text-sm text-muted-foreground">{industry.projects} projects</span>
                      </div>
                      <div className="h-1.5 md:h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className={`h-full bg-gradient-to-r ${progressGradient} rounded-full transition-all duration-1000`}
                          style={{ width: `${industry.percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="slide-left">
            <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6 mt-6 lg:mt-0">
              <div className="space-y-3 sm:space-y-4 md:space-y-6">
                <StatBoxComponent stat={statBoxes[0]} />
                <StatBoxComponent stat={statBoxes[1]} />
              </div>
              <div className="space-y-3 sm:space-y-4 md:space-y-6 mt-4 sm:mt-6 md:mt-8">
                <StatBoxComponent stat={statBoxes[2]} />
                <StatBoxComponent stat={statBoxes[3]} />
              </div>
            </div>
          </AnimateOnScroll>
        </div>
      </div>
    </section>
  );
};

const StatBoxComponent = ({ stat }: { stat: StatBox }) => {
  const Icon = stat.icon;
  
  if (stat.gradient) {
    return (
      <div className={`bg-gradient-to-br ${stat.gradient} rounded-xl md:rounded-2xl p-4 sm:p-5 md:p-6 text-white`}>
        <Icon className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 mb-2 md:mb-3 opacity-80" />
        <div className="text-xl sm:text-2xl md:text-3xl font-bold mb-0.5 md:mb-1">{stat.value}</div>
        <div className="text-xs sm:text-sm opacity-80">{stat.label}</div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl md:rounded-2xl p-4 sm:p-5 md:p-6 shadow-md md:shadow-lg border border-border">
      <Icon className={`w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 mb-2 md:mb-3 text-${stat.iconColor}-500`} />
      <div className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-0.5 md:mb-1">{stat.value}</div>
      <div className="text-xs sm:text-sm text-muted-foreground">{stat.label}</div>
    </div>
  );
};
